import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import filter from 'lodash/filter';
import find from 'lodash/find';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { openConfirmDialog } from '../../../../store/dialogs';
import { putDemandMeter } from '../../../../store/demand';

// ─── helpers ──────────────────────────────────────────────────────────────────

function effectiveStatus(meter, rto) {
  if (meter.shed === false) return 'exempt';
  if (meter.shed === true) return 'forced';
  return rto?.shed ? 'active' : 'auto';
}

const STATUS_CONFIG = {
  exempt: { label: 'Exempt', color: 'info', variant: 'filled' },
  forced: { label: 'Forced Active', color: 'error', variant: 'filled' },
  active: { label: 'Active (RTO)', color: 'warning', variant: 'filled' },
  auto: { label: 'Auto', color: 'default', variant: 'outlined' },
};

// ─── MeterRow ─────────────────────────────────────────────────────────────────

function MeterRow({ meter, siteName, rto }) {
  const dispatch = useDispatch();
  const status = effectiveStatus(meter, rto);
  const sc = STATUS_CONFIG[status];
  const isExempt = meter.shed === false;

  const handleToggleExempt = () => {
    dispatch(putDemandMeter({ ...meter, shed: isExempt ? null : false }));
  };

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant='body2' fontWeight={500}>
          {meter.name}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2' color='text.secondary'>
          {siteName ?? '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip size='small' label={sc.label} color={sc.color} variant={sc.variant} />
      </TableCell>
      <TableCell align='center'>
        <Tooltip
          title={
            isExempt
              ? 'Exempt — will not shed regardless of grid signal. Click to reset to auto.'
              : 'Follows grid signal — click to exempt from load shed.'
          }>
          <Switch
            size='small'
            checked={isExempt}
            onChange={handleToggleExempt}
            color='info'
          />
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

// ─── Buildings ────────────────────────────────────────────────────────────────

export default function Buildings() {
  const dispatch = useDispatch();
  const { organizations, sites, meters, rtos } = useSelector((state) => state.demand);

  const [selectedOrgId, setSelectedOrgId] = useState('');

  const nonPortfolioOrgs = useMemo(
    () => sortBy(filter(organizations, (o) => !o.is_portfolio), 'name'),
    [organizations]
  );

  const rto = useMemo(() => find(rtos, (r) => r.rto_id === 'PJM'), [rtos]);

  const visibleMeters = useMemo(() => {
    const base = filter(meters, (m) => m.rto_id === rto?.rto_id || !selectedOrgId);
    return selectedOrgId ? filter(base, { org_id: selectedOrgId }) : base;
  }, [meters, rto, selectedOrgId]);

  const groupedByOrg = useMemo(() => groupBy(visibleMeters, 'org_id'), [visibleMeters]);

  const orgGroups = useMemo(() => {
    return sortBy(
      Object.keys(groupedByOrg).map((orgId) => ({
        org: find(organizations, { org_id: orgId }),
        meters: sortBy(groupedByOrg[orgId], 'name'),
      })),
      (g) => g.org?.name ?? ''
    );
  }, [groupedByOrg, organizations]);

  const exemptCount = useMemo(() => filter(visibleMeters, { shed: false }).length, [visibleMeters]);

  const handleExemptAll = () => {
    const targets = filter(visibleMeters, (m) => m.shed !== false);
    if (!targets.length) return;
    dispatch(
      openConfirmDialog({
        title: 'Exempt All Meters',
        message: (
          <p>
            Set <strong>{targets.length}</strong> meter
            {targets.length !== 1 ? 's' : ''} to <strong>Exempt</strong>?
            They will not shed regardless of the grid signal.
          </p>
        ),
        onConfirm: () =>
          targets.forEach((m) => dispatch(putDemandMeter({ ...m, shed: false }))),
      })
    );
  };

  const handleResetAll = () => {
    const targets = filter(visibleMeters, (m) => m.shed !== null);
    if (!targets.length) return;
    dispatch(
      openConfirmDialog({
        title: 'Reset All to Auto',
        message: (
          <p>
            Reset <strong>{targets.length}</strong> meter
            {targets.length !== 1 ? 's' : ''} to <strong>Auto</strong>?
            They will follow the grid signal.
          </p>
        ),
        onConfirm: () =>
          targets.forEach((m) => dispatch(putDemandMeter({ ...m, shed: null }))),
      })
    );
  };

  const handleForceShedAll = () => {
    const targets = filter(visibleMeters, (m) => m.shed !== true);
    if (!targets.length) return;
    dispatch(
      openConfirmDialog({
        title: 'Force Load Shed — Testing',
        message: (
          <p>
            Force <strong>{targets.length}</strong> meter
            {targets.length !== 1 ? 's' : ''} to <strong>Active</strong>{' '}
            regardless of the grid signal? Use this to manually test field
            device load shed behavior.
          </p>
        ),
        onConfirm: () =>
          targets.forEach((m) => dispatch(putDemandMeter({ ...m, shed: true }))),
      })
    );
  };

  return (
    <Box sx={{ maxWidth: 900, width: '100%', px: 1, mt: 1, mb: 5 }}>
      {/* Controls */}
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <FormControl size='small' sx={{ minWidth: 200 }}>
            <InputLabel>Organization</InputLabel>
            <Select
              value={selectedOrgId}
              label='Organization'
              onChange={(e) => setSelectedOrgId(e.target.value)}>
              <MenuItem value=''>All Organizations</MenuItem>
              {map(nonPortfolioOrgs, (org) => (
                <MenuItem key={org.org_id} value={org.org_id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {exemptCount > 0 && (
            <Chip
              size='small'
              label={`${exemptCount} exempt`}
              color='info'
            />
          )}
        </Stack>
        <Stack direction='row' spacing={1}>
          <Button size='small' variant='outlined' color='error' onClick={handleForceShedAll}>
            Force Shed (Test)
          </Button>
          <Button size='small' variant='outlined' color='info' onClick={handleExemptAll}>
            Exempt All
          </Button>
          <Button size='small' variant='outlined' color='inherit' onClick={handleResetAll}>
            Reset to Auto
          </Button>
        </Stack>
      </Stack>

      {/* Meter tables grouped by org */}
      <Stack spacing={2}>
        {orgGroups.map(({ org, meters: orgMeters }) => (
          <Card key={org?.org_id} variant='outlined'>
            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant='subtitle2' fontWeight={600}>
                {org?.name ?? org?.org_id}
              </Typography>
            </Box>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Meter</TableCell>
                  <TableCell>Site</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align='center'>Shed Exempt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {map(orgMeters, (meter) => {
                  const site = find(sites, { site_id: meter.site_id });
                  return (
                    <MeterRow
                      key={meter.meter_id}
                      meter={meter}
                      siteName={site?.name}
                      rto={rto}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        ))}
        {orgGroups.length === 0 && (
          <Typography variant='body2' color='text.secondary'>
            No meters found.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}